/**
 * Graph ownership for the demo engine: building the audio context on demand,
 * the master volume, the vinyl crackle bed, and the one-shot interface sounds.
 *
 * Kept apart from the transport so the engine itself is only the clock and
 * the scheduler.
 */

import { Emitter } from '../../lib/emitter.ts';
import { clamp } from '../../lib/math.ts';
import type { EngineEvents } from '../engine.ts';
import { buildGraph, type DemoGraph, readLevel } from './graph.ts';
import { armRest, click, needleDrop, powerDown } from './sfx.ts';

const CRACKLE_LEVEL = 0.055;

export abstract class DemoAudioHost extends Emitter<EngineEvents> {
  graph: DemoGraph | null = null;
  volume = 0.85;
  crackleOn = true;

  #booting: Promise<DemoGraph> | null = null;

  /** The transport decides this; the crackle bed follows it. */
  abstract readonly playing: boolean;

  get ctx(): AudioContext | null {
    return this.graph?.ctx ?? null;
  }

  get ready(): boolean {
    return this.graph !== null;
  }

  get level(): number {
    return this.graph && this.playing ? readLevel(this.graph) : 0;
  }

  /** AudioContext time, or 0 before the graph exists. */
  protected get now(): number {
    return this.graph?.ctx.currentTime ?? 0;
  }

  /**
   * Builds the audio graph. Safe to call before any user gesture — the context
   * simply stays suspended until one arrives. `resume()` never settles while a
   * page is gesture-locked, so it is raced against a timeout, never awaited.
   */
  async ensure(): Promise<DemoGraph> {
    if (this.graph) {
      await this.#resume();
      return this.graph;
    }
    this.#booting ??= this.#boot();
    try {
      return await this.#booting;
    } catch (err) {
      this.#booting = null; /* allow a retry after a failed start */
      throw err;
    }
  }

  setVolume(v: number): void {
    this.volume = clamp(v, 0, 1);
    const g = this.graph;
    if (g) g.master.gain.setTargetAtTime(this.volume, g.ctx.currentTime, 0.03);
  }

  setCrackle(on: boolean): void {
    this.crackleOn = on;
    this.fadeCrackle(on && this.playing ? CRACKLE_LEVEL : 0, 0.25);
  }

  protected fadeCrackle(to: number, time: number): void {
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    g.crackle.gain.cancelScheduledValues(t);
    g.crackle.gain.setTargetAtTime(to, t, time);
  }

  async sfxNeedleDrop(): Promise<void> {
    needleDrop(await this.ensure());
  }

  async sfxClick(pitch = 1): Promise<void> {
    click(await this.ensure(), pitch);
  }

  async sfxPowerDown(): Promise<void> {
    powerDown(await this.ensure());
  }

  async sfxArmRest(): Promise<void> {
    armRest(await this.ensure());
  }

  destroy(): void {
    try {
      this.graph?.crackleSrc.stop();
    } catch {
      /* already stopped */
    }
    void this.graph?.ctx.close().catch(() => undefined);
    this.graph = null;
  }

  async #boot(): Promise<DemoGraph> {
    const Ctor = globalThis.AudioContext;
    if (!Ctor) throw new Error('Web Audio is not available in this browser.');
    this.graph = buildGraph(new Ctor({ latencyHint: 'interactive' }), this.volume);
    await this.#resume();
    return this.graph;
  }

  async #resume(): Promise<void> {
    const ctx = this.graph?.ctx;
    if (!ctx || ctx.state === 'running') return;
    await Promise.race([
      ctx.resume().catch(() => undefined),
      new Promise<void>((r) => {
        setTimeout(r, 1200);
      }),
    ]);
  }
}
